import React, { useEffect, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import api from "../../api";
import { toast } from "../../hooks/use-toast";
import { copyText } from "../../lib/copy";
import { Loader2, Trash2, Copy, Smartphone, UserPlus } from "lucide-react";

const input = "rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm";
const card = "bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]";
const err = (e) => toast({ title: "Error", description: e?.response?.data?.detail || "Failed", variant: "destructive" });

const AppLink = ({ slug }) => {
  const url = `${window.location.origin}/staff/${slug}`;
  return (
    <div className={`${card} flex flex-col sm:flex-row gap-6 items-center`} data-testid="staff-app-link-card">
      <div className="bg-white p-2 rounded-xl border border-gray-100"><QRCodeSVG value={url} size={128} /></div>
      <div className="flex-1">
        <p className="font-semibold text-[#16243b] flex items-center gap-2"><Smartphone size={18} className="text-[#2ec9c9]" /> Open the staff app on any phone</p>
        <p className="text-sm text-[#5a6b80] mt-1">Scan this code or open the link, then sign in with a staff PIN. Staff can only scan, redeem and sell — they never see your dashboard.</p>
        <div className="flex gap-2 mt-3">
          <code className="text-xs bg-[#f6f8fb] rounded-lg px-3 py-2 flex-1 truncate" data-testid="staff-app-url">{url}</code>
          <button onClick={() => copyText(url, "Link copied")} data-testid="copy-staff-link" className="gp-btn-outline rounded-lg px-3"><Copy size={15} /></button>
        </div>
      </div>
    </div>
  );
};

const MemberRow = ({ m, onChange, onDelete }) => {
  const [pin, setPin] = useState("");
  const save = (patch) => onChange(m, { name: m.name, can_sell: m.can_sell, active: m.active, pin: "", ...patch });
  return (
    <tr className="border-t border-gray-100" data-testid={`team-row-${m.id}`}>
      <td className="py-3 font-medium text-[#16243b]">{m.name}<p className="text-xs text-[#8a97a8] font-normal">{m.last_login_at ? `Last in ${m.last_login_at.slice(0, 10)}` : "Never signed in"}</p></td>
      <td><input type="checkbox" checked={m.can_sell} onChange={(e) => save({ can_sell: e.target.checked })} data-testid={`team-cansell-${m.id}`} /></td>
      <td><input type="checkbox" checked={m.active} onChange={(e) => save({ active: e.target.checked })} data-testid={`team-active-${m.id}`} /></td>
      <td className="flex gap-2 py-3">
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} maxLength={8} placeholder="New PIN" className={`${input} w-28 py-1.5`} data-testid={`team-pin-${m.id}`} />
        <button disabled={pin.length < 4} onClick={() => { save({ pin }); setPin(""); }} className="gp-btn-outline rounded-lg px-3 text-xs disabled:opacity-40" data-testid={`team-pin-save-${m.id}`}>Set</button>
        <button onClick={() => onDelete(m)} className="text-red-500 px-2" data-testid={`team-delete-${m.id}`}><Trash2 size={16} /></button>
      </td>
    </tr>
  );
};

const Activity = ({ rows }) => (
  <div className={card} data-testid="team-activity">
    <h3 className="font-semibold text-[#16243b] mb-3">Recent staff activity</h3>
    {rows.length === 0 ? <p className="text-sm text-[#8a97a8]">No staff redemptions or in-store sales yet.</p> : (
      <ul className="divide-y divide-gray-100 text-sm">
        {rows.map((r, i) => (
          <li key={i} className="py-2 flex justify-between">
            <span><span className={`text-xs font-semibold px-2 py-0.5 rounded-full mr-2 ${r.kind === "sale" ? "bg-[#eefafa] text-[#1fb0b0]" : "bg-amber-50 text-amber-700"}`}>{r.kind === "sale" ? "Sold" : "Redeemed"}</span>
              <span className="font-mono">{r.code}</span> · {r.by}{r.method ? ` · ${r.method}` : ""}</span>
            <span className="text-[#16243b] font-medium">£{Number(r.amount).toFixed(2)} <span className="text-xs text-[#8a97a8] font-normal ml-2">{r.at.slice(0, 16).replace("T", " ")}</span></span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function TeamTab() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [f, setF] = useState({ name: "", pin: "", can_sell: true });
  const load = useCallback(() => {
    api.get("/business/team").then(({ data }) => setData(data)).catch(() => setData({ members: [], slug: "" }));
    api.get("/business/team/activity").then(({ data }) => setActivity(data.activity)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/business/team", { ...f, active: true }); setF({ name: "", pin: "", can_sell: true }); toast({ title: "Team member added" }); load(); } catch (ex) { err(ex); }
  };
  const change = async (m, body) => { try { await api.put(`/business/team/${m.id}`, body); toast({ title: "Saved" }); load(); } catch (ex) { err(ex); } };
  const remove = async (m) => { if (!window.confirm(`Remove ${m.name}?`)) return; try { await api.delete(`/business/team/${m.id}`); load(); } catch (ex) { err(ex); } };

  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  return (
    <div className="space-y-6 max-w-[900px]" data-testid="team-tab">
      {data.slug && <AppLink slug={data.slug} />}
      <div className={card}>
        <h3 className="font-semibold text-[#16243b] mb-3">Staff & PINs</h3>
        <form onSubmit={add} className="flex flex-wrap gap-2 mb-4">
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={`${input} flex-1 min-w-[160px]`} data-testid="team-name-input" />
          <input required value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, "") })} maxLength={8} placeholder="PIN (4–8 digits)" className={`${input} w-40`} data-testid="team-pin-input" />
          <label className="flex items-center gap-2 text-sm text-[#5a6b80] px-2"><input type="checkbox" checked={f.can_sell} onChange={(e) => setF({ ...f, can_sell: e.target.checked })} data-testid="team-cansell-input" /> Can sell</label>
          <button className="gp-btn-teal rounded-xl px-4 text-sm font-medium flex items-center gap-2" data-testid="team-add-btn"><UserPlus size={16} /> Add</button>
        </form>
        {data.members.length === 0 ? <p className="text-sm text-[#8a97a8]">No staff yet. Add someone above to give them a PIN.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#8a97a8]"><th className="py-2">Name</th><th>Can sell</th><th>Active</th><th>PIN</th></tr></thead>
            <tbody>{data.members.map((m) => <MemberRow key={m.id} m={m} onChange={change} onDelete={remove} />)}</tbody>
          </table>
        )}
      </div>
      <Activity rows={activity} />
    </div>
  );
}
